import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { BriefcaseIcon, PlusIcon } from "@phosphor-icons/react";
import type { Experience } from "@reactive-resume/schema";

import { ExperienceCard } from "./experience-card";

type Props = {
  items: Experience[];
  onAdd: () => void;
  onEdit: (item: Experience) => void;
  onDelete: (id: string) => void;
  onReorder: (activeId: string, overId: string) => void;
};

export const ExperienceList = ({ items, onAdd, onEdit, onDelete, onReorder }: Props) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over.id) onReorder(String(active.id), String(over.id));
  };

  if (!items.length) {
    return (
      <div className="rounded-2xl border border-dashed border-indigo-200 bg-indigo-50/30 px-5 py-9 text-center">
        <span className="mx-auto grid size-11 place-items-center rounded-xl bg-white text-indigo-600 shadow-sm">
          <BriefcaseIcon size={23} weight="duotone" />
        </span>
        <h3 className="mt-3 text-sm font-bold text-indigo-950">Aucune expérience ajoutée</h3>
        <p className="mx-auto mt-1 max-w-sm text-[11px] leading-5 text-slate-500">
          Ajoutez un emploi, un stage, une mission freelance ou une expérience bénévole.
        </p>
        <button
          type="button"
          onClick={onAdd}
          className="mt-4 inline-flex min-h-10 items-center gap-2 rounded-xl bg-indigo-700 px-4 text-xs font-bold text-white hover:bg-indigo-800"
        >
          <PlusIcon size={15} weight="bold" /> Ajouter une expérience
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-xs font-bold text-indigo-950 sm:text-sm">Vos expériences</h3>
          <p className="mt-0.5 text-[10px] text-slate-500">
            Faites glisser une carte pour modifier son ordre.
          </p>
        </div>
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex min-h-9 shrink-0 items-center gap-1.5 rounded-xl border border-indigo-200 px-3 text-[10px] font-bold text-indigo-700 hover:bg-indigo-50"
        >
          <PlusIcon size={14} weight="bold" /> Ajouter
        </button>
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToParentElement]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2.5">
            {items.map((item) => (
              <ExperienceCard key={item.id} item={item} onEdit={onEdit} onDelete={onDelete} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};
